import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useSettings } from '../hooks/useSettings';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import RichTextEditor from './RichTextEditor';
import { Plus, FileText } from 'lucide-react';

// Import sub-components
import { StatsCards, SearchFilters, PatientList, PatientFormDialog, renderCustomFieldInput } from './MesEleves/index';
import { NoteFormDialog } from './MesNotes/index';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MesEleves = () => {
  const { 
    getPatientLabel, 
    getPatientLabelPlural, 
    getPatientLabelCapitalized, 
    getPatientLabelPluralCapitalized, 
    getNewPatientLabel, 
    getPatientArticle 
  } = useSettings();

  // Core state
  const [stats, setStats] = useState({ total_patients: 0, total_groupes: 0, total_notes: 0 });
  const [patients, setPatients] = useState([]);
  const [groupes, setGroupes] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search & filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroupe, setSelectedGroupe] = useState('all');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Dialog states
  const [isCreatePatientOpen, setIsCreatePatientOpen] = useState(false);
  const [isEditPatientOpen, setIsEditPatientOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientNotes, setPatientNotes] = useState([]);
  const [isCreateNoteOpen, setIsCreateNoteOpen] = useState(false);
  const [isQuickNoteOpen, setIsQuickNoteOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);

  // Form states
  const [newPatient, setNewPatient] = useState({
    nom: '', prenom: '', groupe_id: '', custom_fields: {}
  });
  const [editPatient, setEditPatient] = useState({
    nom: '', prenom: '', groupe_id: '', custom_fields: {}
  });
  const [newNote, setNewNote] = useState({ titre: '', contenu: '' });
  const [quickNote, setQuickNote] = useState({ patient_id: '', titre: '', contenu: '' });
  const [editNote, setEditNote] = useState({ titre: '', contenu: '' });

  // Load data
  const loadData = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      
      const [statsRes, patientsRes, groupesRes, customFieldsRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/patients`),
        axios.get(`${API}/groupes`),
        axios.get(`${API}/custom-fields`)
      ]);
      
      setStats(statsRes.data);
      setPatients(patientsRes.data);
      setGroupes(groupesRes.data);
      setCustomFields(customFieldsRes.data.filter(f => f.is_active));
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter and sort patients
  const filteredPatients = useMemo(() => {
    let filtered = [...patients];
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.nom?.toLowerCase().includes(search) ||
        p.prenom?.toLowerCase().includes(search) ||
        Object.values(p.custom_fields || {}).some(v => 
          String(v).toLowerCase().includes(search)
        )
      );
    }
    
    // Group filter
    if (selectedGroupe !== 'all') {
      if (selectedGroupe === 'none') {
        filtered = filtered.filter(p => !p.groupe_id);
      } else {
        filtered = filtered.filter(p => p.groupe_id === selectedGroupe);
      }
    }
    
    // Sort alphabetically
    return filtered.sort((a, b) => {
      const nameA = `${a.prenom} ${a.nom}`.toLowerCase();
      const nameB = `${b.prenom} ${b.nom}`.toLowerCase();
      return nameA.localeCompare(nameB, 'fr');
    });
  }, [patients, searchTerm, selectedGroupe]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedGroupe]);

  // CRUD Operations
  const handleCreatePatient = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/patients`, newPatient);
      toast.success(`${getPatientLabelCapitalized()} créé avec succès`);
      setNewPatient({ nom: '', prenom: '', groupe_id: '', custom_fields: {} });
      setIsCreatePatientOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleEditPatient = async (e) => {
    e.preventDefault();
    if (!editingPatient) return;
    try {
      await axios.put(`${API}/patients/${editingPatient.id}`, editPatient);
      toast.success(`${getPatientLabelCapitalized()} modifié avec succès`);
      setIsEditPatientOpen(false);
      setEditingPatient(null);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDeletePatient = async (patient) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${getPatientArticle()} ${getPatientLabel()} ?`)) return;
    try {
      await axios.delete(`${API}/patients/${patient.id}`);
      toast.success(`${getPatientLabelCapitalized()} supprimé avec succès`);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEditClick = (patient) => {
    setEditingPatient(patient);
    setEditPatient({
      nom: patient.nom,
      prenom: patient.prenom,
      groupe_id: patient.groupe_id || '',
      custom_fields: patient.custom_fields || {}
    });
    setIsEditPatientOpen(true);
  };

  const handleViewNotes = async (patient) => {
    setSelectedPatient(patient);
    try {
      const res = await axios.get(`${API}/patients/${patient.id}/notes`);
      setPatientNotes(res.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      setIsNotesModalOpen(true);
    } catch (error) {
      toast.error('Erreur lors du chargement des notes');
    }
  };

  // Note operations
  const handleCreateNote = async (e) => {
    e.preventDefault();
    if (!selectedPatient) return;
    try {
      await axios.post(`${API}/notes`, {
        patient_id: selectedPatient.id,
        titre: newNote.titre,
        contenu: newNote.contenu
      });
      toast.success('Note créée avec succès');
      setNewNote({ titre: '', contenu: '' });
      setIsCreateNoteOpen(false);
      const res = await axios.get(`${API}/patients/${selectedPatient.id}/notes`);
      setPatientNotes(res.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      loadData(true);
    } catch (error) {
      toast.error('Erreur lors de la création de la note');
    }
  };

  const handleCreateQuickNote = async (e) => {
    e.preventDefault();
    if (!quickNote.patient_id) {
      toast.error(`Veuillez sélectionner un ${getPatientLabel()}`);
      return;
    }
    try {
      await axios.post(`${API}/notes`, quickNote);
      toast.success('Note créée avec succès');
      setQuickNote({ patient_id: '', titre: '', contenu: '' });
      setIsQuickNoteOpen(false);
      loadData(true);
    } catch (error) {
      toast.error('Erreur lors de la création de la note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette note ?')) return;
    try {
      await axios.delete(`${API}/notes/${noteId}`);
      toast.success('Note supprimée avec succès');
      if (selectedPatient) {
        const res = await axios.get(`${API}/patients/${selectedPatient.id}/notes`);
        setPatientNotes(res.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      }
      loadData(true);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEditNote = async (e) => {
    e.preventDefault();
    if (!editingNote) return;
    try {
      await axios.put(`${API}/notes/${editingNote.id}`, editNote);
      toast.success('Note modifiée avec succès');
      setEditingNote(null);
      if (selectedPatient) {
        const res = await axios.get(`${API}/patients/${selectedPatient.id}/notes`);
        setPatientNotes(res.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      }
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleStartEditNote = (note) => {
    setEditingNote(note);
    setEditNote({ titre: note.titre, contenu: note.contenu });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <StatsCards stats={stats} patientLabelPlural={getPatientLabelPluralCapitalized()} />

      {/* Actions */}
      <div className="flex justify-end gap-3">
        {/* Quick Note Button */}
        <Dialog open={isQuickNoteOpen} onOpenChange={setIsQuickNoteOpen}>
          <DialogTrigger asChild>
            <Button variant="secondary" className="flex items-center gap-2" data-testid="quick-note-btn">
              <FileText className="h-4 w-4" />
              Nouvelle Note
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Créer une nouvelle note</DialogTitle>
              <DialogDescription>Rédiger une note pour un {getPatientLabel()}</DialogDescription>
            </DialogHeader>
            <NoteFormDialog
              isOpen={true}
              onOpenChange={() => {}}
              note={quickNote}
              setNote={setQuickNote}
              patients={patients}
              noteStatuses={[]}
              statusesEnabled={false}
              onSubmit={handleCreateQuickNote}
              patientLabel={getPatientLabel()}
            />
          </DialogContent>
        </Dialog>

        {/* Create Patient Button */}
        <PatientFormDialog
          isOpen={isCreatePatientOpen}
          onOpenChange={setIsCreatePatientOpen}
          patient={newPatient}
          setPatient={setNewPatient}
          groupes={groupes}
          customFields={customFields}
          onSubmit={handleCreatePatient}
          patientLabel={getPatientLabel()}
          triggerButton={
            <Button className="flex items-center gap-2" data-testid="create-patient-btn">
              <Plus className="h-4 w-4" />
              {getNewPatientLabel()}
            </Button>
          }
        />
      </div>

      {/* Search & Filters */}
      <SearchFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedGroupe={selectedGroupe}
        setSelectedGroupe={setSelectedGroupe}
        groupes={groupes}
        patientLabel={getPatientLabel()}
      />

      {/* Patient List */}
      <PatientList
        patients={filteredPatients}
        groupes={groupes}
        customFields={customFields}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onEdit={handleEditClick}
        onDelete={handleDeletePatient}
        onViewNotes={handleViewNotes}
        patientLabel={getPatientLabel()}
        patientLabelPlural={getPatientLabelPlural()}
        patientArticle={getPatientArticle()}
      />

      {/* Edit Patient Dialog */}
      <PatientFormDialog
        isOpen={isEditPatientOpen}
        onOpenChange={setIsEditPatientOpen}
        patient={editPatient}
        setPatient={setEditPatient}
        groupes={groupes}
        customFields={customFields}
        onSubmit={handleEditPatient}
        isEditing={true}
        patientLabel={getPatientLabel()}
      />

      {/* Notes Modal */}
      <Dialog open={isNotesModalOpen} onOpenChange={setIsNotesModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Notes de {selectedPatient?.prenom} {selectedPatient?.nom}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Button onClick={() => setIsCreateNoteOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle note
            </Button>

            {patientNotes.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Aucune note pour {getPatientArticle()} {getPatientLabel()}</p>
            ) : (
              <div className="space-y-4">
                {patientNotes.map(note => (
                  <div key={note.id} className="border rounded-lg p-4">
                    {editingNote?.id === note.id ? (
                      <form onSubmit={handleEditNote} className="space-y-3">
                        <input
                          value={editNote.titre}
                          onChange={(e) => setEditNote({...editNote, titre: e.target.value})}
                          className="w-full p-2 border rounded"
                          required
                        />
                        <RichTextEditor
                          value={editNote.contenu}
                          onChange={(v) => setEditNote(prev => ({...prev, contenu: v}))}
                        />
                        <div className="flex gap-2">
                          <Button type="submit" size="sm">Sauvegarder</Button>
                          <Button type="button" variant="outline" size="sm" onClick={() => setEditingNote(null)}>
                            Annuler
                          </Button>
                        </div>
                      </form>
                    ) : (
                      <>
                        <div className="flex justify-between items-start">
                          <h4 className="font-medium">{note.titre}</h4>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handleStartEditNote(note)}>
                              Modifier
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteNote(note.id)}>
                              Supprimer
                            </Button>
                          </div>
                        </div>
                        <div className="prose prose-sm mt-2" dangerouslySetInnerHTML={{ __html: note.contenu }} />
                        <p className="text-xs text-gray-500 mt-2">
                          {new Date(note.created_at).toLocaleDateString('fr-FR', {
                            day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Note Dialog (within patient context) */}
      <Dialog open={isCreateNoteOpen} onOpenChange={setIsCreateNoteOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Nouvelle note</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateNote} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Titre *</label>
              <input
                value={newNote.titre}
                onChange={(e) => setNewNote({...newNote, titre: e.target.value})}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Contenu *</label>
              <RichTextEditor
                value={newNote.contenu}
                onChange={(v) => setNewNote(prev => ({...prev, contenu: v}))}
              />
            </div>
            <Button type="submit" className="w-full">Créer la note</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MesEleves;
